"use client"
import Text from "@/app/(site)/_components/Atoms/Text"
import AddToCartButton from "@/app/(site)/_components/Modules/ProductDetail/AddToCartButton"
import ColorSwatches from "@/app/(site)/_components/Modules/ProductDetail/ColorSwatches"
import LensGuide from "@/app/(site)/_components/Modules/ProductDetail/LensGuide/LensGuide"
import SelectedSize from "@/app/(site)/_components/Modules/ProductDetail/SelectedSize/SelectedSize"
import SizeChart from "@/app/(site)/_components/Modules/ProductDetail/SizeChart"
import SizeGuide from "@/app/(site)/_components/Modules/ProductDetail/SizeGuide/SizeGuide"
import { useEyeFitUIEvent } from "@/app/(site)/_utils/_hooks/useEyeFitUIEvent"
import useOneTrustOptInLoad from "@/app/(site)/_utils/_hooks/useOneTrustOptInLoad"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { useAppSelector } from "@/app/(site)/contexts/Global"
import { viewItemDepictEvent } from "@/lib/utils/tracking/depictEvents"
import { viewItemEvent } from "@/lib/utils/tracking/gtmEvents"
import { extractProductHierarchy } from "@/lib/utils/tracking/productHierarchyUtils"
import { t } from "@/translations"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import placeholder from "@assets/images/placeholder.png"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import FitType from "@/app/(site)/_components/Modules/ProductDetail/FitType"
import { useLocaleFromPathname } from "@/app/(site)/_utils/_hooks/useLocaleFromPathname"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { selectSize, useAppDispatch } from "@/app/(site)/contexts/Global"
import {
    ProductData,
    RelatedVariant,
    SizeInfo
} from "@/lib/content-service/types/ContentServiceProduct"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"

type ProductFormType = {
    relatedVariantsFitType?: string[]
    productFitType?: string
    product: CheckoutAPIProduct | RelatedProduct | ProductData
    relatedVariants: RelatedProduct[] | RelatedVariant[]
    firstAvailableSize?: SizeInfo
    sizes: SizeInfo[]
    params: { slug: string; locale: string }
}

interface DataForEyeFitUIEventDetail {
    canonicalCategory: string
    gender: string
    sizeLabels: string[]
    mainImage: string
    productName: string
}

export default function ProductForm({
    relatedVariantsFitType,
    productFitType,
    product,
    relatedVariants,
    firstAvailableSize,
    sizes,
    params
}: ProductFormType) {
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const langCode = getLangCode(params.locale) || "en"
    const { functionalConsent } = useOneTrustOptInLoad()
    const siteContext = useSiteContext()
    const centraSession = useEcommerceStore(state => state.centraSession)
    const dispatch = useAppDispatch()

    const [errorStyles, setErrorStyles] = useState(false)
    const [dataForEyeFitUI, setDataForEyeFitUI] = useState<DataForEyeFitUIEventDetail>()
    const [hasLoaded, setHasLoaded] = useState(false)
    const [fitType, setFitType] = useState(productFitType || "")
    const hasTrackedView = useRef(false)
    const hasSetInitialSize = useRef(false)
    const previousProductSku = useRef<string>("")
    const previousPathname = useRef<string>("")

    const [filteredAllVariants, setFilteredAllVariants] = useState<
        (CheckoutAPIProduct | RelatedProduct | ProductData | RelatedVariant)[]
    >([...relatedVariants, product])

    // Memoize derived values to prevent unnecessary re-renders
    const canonicalCategory = useMemo(
        () =>
            Array.isArray(product?.categoryName)
                ? product.categoryName.map(c => c.toLowerCase()).join("/")
                : "",
        [product?.categoryName]
    )

    const productColorName = product?.color_swatch?.color_name
    const productLensName = product?.lens?.lens_name
    const isOneSize = useMemo(
        () =>
            Array.isArray(sizes) &&
            sizes.length > 0 &&
            sizes.every(({ name }) => name === "One Size") &&
            sizes.length === 1,
        [sizes]
    )
    const isFitType = useMemo(
        () => Array.isArray(relatedVariantsFitType) && relatedVariantsFitType.length > 0,
        [relatedVariantsFitType]
    )

    const sizeLabels = useMemo(() => sizes.map(size => size.name) || [], [sizes])

    // Get the currently selected size from the global state (only when needed)
    const availableSizesContext = useAppSelector(state => state.product.availableSizes)
    const selectedSize = useAppSelector(state => state.product.selectedSize)

    // Memoize product SKU to prevent unnecessary effect triggers
    const productSku = useMemo(() => product.sku, [product.sku])

    // Memoize locale to prevent unnecessary effect triggers
    const locale = useLocaleFromPathname()

    // Memoize product properties to prevent unnecessary re-renders
    const productFit = useMemo(() => product?.fit, [product?.fit])
    const productName = useMemo(() => product?.name, [product?.name])
    const productMedia = useMemo(
        () => (product as ProductData)?.media,
        [(product as ProductData)?.media]
    )

    // Check if navigation came from color swatch
    const isColorSwatchNavigation = searchParams.get("source") === "color_swatch"

    // Reset tracking when product changes (color navigation)
    useEffect(() => {
        if (firstAvailableSize) {
            dispatch(selectSize(firstAvailableSize.name))
        }
        if (productSku !== previousProductSku.current) {
            hasTrackedView.current = false
            hasSetInitialSize.current = false
            previousProductSku.current = productSku
        }
    }, [productSku])

    // Handle initial load and color swatch navigation
    useEffect(() => {
        if (
            !hasTrackedView.current &&
            product?.sku && // Ensure we have valid product data
            product?.name && // Ensure we have valid product data
            centraSession?.token // Ensure centraSession is available
        ) {
            // Determine tracking type based on navigation source
            const trackingType = isColorSwatchNavigation ? "Change Colour" : "Load PDP"

            // Use selectedSize if available, otherwise use firstAvailableSize, or undefined if no size available
            const sizeForTracking = selectedSize || firstAvailableSize?.name || undefined

            // Fire view_item event
            const success = viewItemEvent(
                product,
                { size: sizeForTracking },
                siteContext,
                trackingType,
                extractProductHierarchy(product)
            )

            if (!success) {
                return
            }

            if (functionalConsent) {
                viewItemDepictEvent(product, { size: sizeForTracking })
            }

            hasSetInitialSize.current = true
            hasTrackedView.current = true
            setHasLoaded(true)
            previousProductSku.current = productSku
            previousPathname.current = pathname

            // Clean up the URL parameter after tracking
            if (isColorSwatchNavigation) {
                const url = new URL(window.location.href)
                url.searchParams.delete("source")
                router.replace(url.pathname + url.search, { scroll: false })
            }
        }
    }, [
        firstAvailableSize,
        product,
        siteContext,
        functionalConsent,
        selectedSize,
        productSku,
        pathname,
        centraSession,
        isColorSwatchNavigation,
        router
    ])

    const handleFitTypeChange = useCallback(
        (newFitType: string) => {
            if (product?.fit_type_fit_type !== newFitType) {
                const relevantProduct = (relatedVariants as RelatedVariant[]).find(
                    p => p?.fit_type_fit_type === newFitType
                )
                const newUri = relevantProduct?.uri

                if (newUri) {
                    router.replace(newUri, {
                        // @ts-expect-error: shallow is not in NavigateOptions
                        shallow: true
                    })
                }
            }
        },
        [product?.fit_type_fit_type, relatedVariants, router]
    )

    useEffect(() => {
        handleFitTypeChange(fitType)
    }, [fitType, handleFitTypeChange])

    useEffect(() => {
        if (product?.categoryName && product.categoryName?.length > 0) {
            setDataForEyeFitUI({
                // Last category in the array is the most relevant
                canonicalCategory: canonicalCategory ?? "",
                gender: productFit?.[0]?.label ?? "",
                sizeLabels: sizeLabels ?? "",
                mainImage: productMedia?.standard?.[0]?.url || placeholder?.src,
                productName: productName ?? ""
            })
        }
    }, [
        product.productSku,
        canonicalCategory,
        sizeLabels,
        productFit,
        productName,
        productMedia
    ])

    useEyeFitUIEvent(dataForEyeFitUI || null)

    return (
        <form>
            {/* fit_type_fit_type */}
            {isFitType && (
                <div className="flex gap-8 border-t border-t-main-medium-grey py-16">
                    <Text size="small" weight="regular">
                        {t(langCode, "product_page.fit")}:{" "}
                    </Text>
                    <Text size="small" weight="regular" className="text-main-dark-grey">
                        {productFitType}
                    </Text>
                    <FitType
                        fitType={fitType}
                        setFitType={setFitType}
                        relatedVariantsFitType={relatedVariantsFitType || []}
                        relatedVariants={relatedVariants}
                        setFilteredAllProducts={setFilteredAllVariants}
                    />
                </div>
            )}

            {/* Color Swatches */}
            <div className="border-t border-t-main-medium-grey py-16">
                <div className="mb-8 flex gap-4">
                    <Text size="small" weight="regular">
                        {t(langCode, "product_page.color")}:{" "}
                    </Text>
                    <Text size="small" weight="regular" className="text-main-dark-grey">
                        {productColorName}
                    </Text>
                </div>
                {product?.lens && (
                    <div className="mb-8 flex gap-4">
                        <Text size="small" weight="regular">
                            {t(langCode, "product_page.lens")}:{" "}
                        </Text>
                        <Text
                            size="small"
                            weight="regular"
                            className="text-main-dark-grey"
                        >
                            {productLensName || ""}
                        </Text>
                    </div>
                )}
                <ColorSwatches
                    initialProductValue={filteredAllVariants}
                    sizes={sizes}
                    currentProduct={product}
                />
            </div>

            {/* Size Selection */}
            <div className="border-t border-t-main-medium-grey py-16">
                <div className="mb-8">
                    <SelectedSize
                        initialSelectedSize={firstAvailableSize?.name}
                        isOneSizeSSR={isOneSize}
                    />
                </div>
                <SizeChart
                    sizes={sizes}
                    setErrorStyles={setErrorStyles}
                    errorStyles={errorStyles}
                    initialSizeValue={firstAvailableSize?.name}
                    currentProduct={product}
                    locale={locale}
                />
            </div>
            {product.size_guide_type && (
                <SizeGuide productId={product.sku} productName={product.name} />
            )}
            {/* Lens Guide */}
            {product.lens_guide_type && <LensGuide />}

            {/* Add to Cart Button */}
            <div className="border-t border-t-main-medium-grey py-16">
                <AddToCartButton product={product} setErrorStyles={setErrorStyles} />
            </div>
        </form>
    )
}
